import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, ChevronRight } from "lucide-react";
import {
  FC,
  MouseEventHandler,
  PropsWithChildren,
  type ReactNode,
} from "react";

import { Link } from "react-router-dom";
import { cn } from "../../utils/classes";
import styles from "./breadcrumbs.module.css";

export const Breadcrumbs: FC<PropsWithChildren> = ({ children }) => {
  return <nav className={styles.trail}>{children}</nav>;
};

export type BreadcrumbOption = {
  title: string;
  uri: string;
  selected?: boolean;
  icon?: ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};

export type BreadcrumbProps = {
  title: string;
  showChevron?: boolean;
  uri?: string;
  classes?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  options?: BreadcrumbOption[];
};

export const Breadcrumb: FC<BreadcrumbProps> = ({
  showChevron,
  title,
  uri,
  classes,
  onClick,
  options,
}) => {
  const divClasses =
    "py-2 text-[14px] font-semibold text-ellipsis whitespace-nowrap overflow-hidden";

  const linkClasses =
    "rounded-xs transition-all duration-300 py-2 text-[14px] font-semibold hover:mx-3 text-ellipsis whitespace-nowrap overflow-hidden";

  return (
    <>
      {showChevron && (
        <ChevronRight
          className={cn("-mx-3 mt-0.5", styles.separator)}
          width={16}
          height={16}
        />
      )}
      {options?.length ? (
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              type="button"
              className={cn(
                divClasses,
                styles.item,
                styles.current,
                styles.menuTrigger,
                classes,
              )}
              aria-label={`${title} options`}
            >
              <span>{title}</span>
              <ChevronDown aria-hidden="true" />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align="start"
              side="bottom"
              sideOffset={4}
              className={styles.menu}
            >
              {options.map((option) => (
                <Popover.Close asChild key={option.uri}>
                  <Link
                    to={option.uri}
                    onClick={option.onClick}
                    className={cn(
                      styles.menuOption,
                      option.selected && styles.menuOptionSelected,
                    )}
                    aria-current={option.selected ? "page" : undefined}
                  >
                    <span className={styles.menuOptionLabel}>
                      {option.icon && (
                        <span className={styles.menuOptionIcon}>
                          {option.icon}
                        </span>
                      )}
                      {option.title}
                    </span>
                    {option.selected && <Check aria-hidden="true" />}
                  </Link>
                </Popover.Close>
              ))}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      ) : uri ? (
        <Link
          to={uri}
          onClick={onClick}
          className={cn(linkClasses, styles.item, styles.link, classes)}
        >
          {title}
        </Link>
      ) : (
        <div className={cn(divClasses, styles.item, styles.current, classes)}>
          {title}
        </div>
      )}
    </>
  );
};
